import { OrganizationUser } from '@wonderschool/common-base-types';
import { Dispatch } from 'redux';
import { startProvisioning } from '../../api/firebase/account';
import { assignPin } from '../../api/firebase/tabletPins';
import {
  getUserProfile,
  organizationUserOnListen,
  updateOrganizationUser,
  updateOrganizationUserPin,
  updateUserProfile,
  userOnListen,
} from '../../api/firebase/users';
import { getAuth } from '../../api/firebase2';
import {
  ORGANIZATION_MISSING,
  ORGANIZATION_PROVISIONING_COMPLETE,
  ORGANIZATION_PROVISIONING_STARTED,
  ORGANIZATION_SELECTED,
  USER_PIN_UPDATED,
  USER_PROFILE_FETCHED,
  USER_PROFILE_LISTENER_STARTED,
  USER_PROFILE_UPDATED,
  USER_SAVE_SIGNUP_DATA,
  USER_SIGNED_IN,
  USER_SIGNED_OUT,
  USER_SIGNED_UP,
} from './types';

export const userSignedIn = ({ uid, email, displayName, phoneNumber, photoURL }, claims) => ({
  type: USER_SIGNED_IN,
  user: {
    uid,
    email,
    displayName,
    phoneNumber,
    photoURL,
    claims,
  },
});
export const userSignedOut = () => ({
  type: USER_SIGNED_OUT,
});

export const userSignedUp = () => ({
  type: USER_SIGNED_UP,
});

export const userSaveSignUpData = (signupData) => ({
  type: USER_SAVE_SIGNUP_DATA,
  signupData,
});

export const userProfileFetched = (user) => ({
  type: USER_PROFILE_FETCHED,
  user,
});

export const userProfileUpdated = (user) => ({
  type: USER_PROFILE_UPDATED,
  user,
});

export const organizationProvisioningStarted = () => ({
  type: ORGANIZATION_PROVISIONING_STARTED,
});

export const organizationProvisioningComplete = () => ({
  type: ORGANIZATION_PROVISIONING_COMPLETE,
});

export const userNeedsSetupOrg = () => ({
  type: ORGANIZATION_MISSING,
});

export const userOrganizationSelected = (organization) => ({
  type: ORGANIZATION_SELECTED,
  organization,
});

export const userProfileListenerStarted = (uid: string) => ({
  type: USER_PROFILE_LISTENER_STARTED,
  uid,
});

export const userPinUpdated = () => ({
  type: USER_PIN_UPDATED,
});

export const completeUserSignup =
  (userData, doCreateOrg = false) =>
  async (dispatch) => {
    if (doCreateOrg) {
      if (!(await dispatch(provisionOrganizationProfile(userData)))) {
        throw new Error('Failed to complete user signup');
      }
    }

    return dispatch(userSignedUp());
  };

const clearStuckImpersonation = async (uid: string) => {
  try {
    await updateUserProfile(uid, {
      impersonatedOrganization: null,
      impersonatedDirectorId: null,
    });
  } catch (error) {
    console.error('Failed to clear stuck impersonation for user:', uid, error);
  }
};

type GetUserState = () => { user?: { claims?: Record<string, unknown> } };

const readFreshClaims = async (): Promise<Record<string, unknown> | null> => {
  try {
    const tokenResult = await getAuth().currentUser?.getIdTokenResult(true);
    return (tokenResult?.claims as Record<string, unknown> | undefined) ?? null;
  } catch (error) {
    console.error('Failed to refresh ID token claims:', error);
    return null;
  }
};

export const startUserProfileListener = (uid: string) => (dispatch: Dispatch, getState?: GetUserState) => {
  dispatch(userProfileListenerStarted(uid));

  let currentOrgId: string | null = null;
  let currentImpersonatedOrgId: string | null = null;
  let resolvedFromProjection = false;
  let refreshedClaimsFor: string | null = null;
  let cachedFreshClaims: Record<string, unknown> | undefined;
  let cachedFreshClaimsBasis: Record<string, unknown> | undefined;
  let snapshotGeneration = 0;
  let pendingRefreshFor: string | null = null;
  let disposed = false;
  let unsubscribeOrgUser: (() => void) | null = null;
  let rootUserData: OrganizationUser | null = null;

  const startOrgUserListener = (organizationId: string) => {
    if (unsubscribeOrgUser) {
      unsubscribeOrgUser();
      unsubscribeOrgUser = null;
    }

    currentOrgId = organizationId;

    unsubscribeOrgUser = organizationUserOnListen(
      organizationId,
      uid,
      (orgUser: OrganizationUser) => {
        if (orgUser && rootUserData) {
          const user = {
            ...rootUserData,
            ...orgUser,
            uid,
            organizationId,
          };
          dispatch(userProfileFetched(user));
        } else if (orgUser) {
          dispatch(userProfileFetched({ ...orgUser, uid, organizationId }));
        }
      },
      (error) => {
        console.error('Organization user listener error:', error.message);
      },
      undefined
    );
  };

  const handleRootUser = (rootUser: OrganizationUser, claims: Record<string, unknown> | undefined) => {
    const claimsResolved = !!claims;
    const isSuperAdmin = !!claims?.moxitAdmin;
    const impersonatedOrganization = rootUser?.impersonatedOrganization as string | undefined;

    if (impersonatedOrganization && claimsResolved && !isSuperAdmin) {
      clearStuckImpersonation(uid);
    }

    const honorImpersonation = !!impersonatedOrganization && (isSuperAdmin || !claimsResolved);

    const multiRoleEnabled = claims?.multi_role_enabled === true;
    const currentOrganization = rootUser?.currentOrganization as string | undefined;
    const projectedOrganization =
      multiRoleEnabled && currentOrganization && rootUser?.organizations?.[currentOrganization] === true
        ? currentOrganization
        : undefined;

    const organizationId = ((honorImpersonation && impersonatedOrganization) ||
      projectedOrganization ||
      rootUser.defaultOrganization) as string;

    const followsProjection = multiRoleEnabled || resolvedFromProjection;

    if (currentOrgId && currentOrgId !== organizationId && followsProjection && !claimsResolved) {
      return;
    }

    const impersonationChanged = (impersonatedOrganization ?? null) !== currentImpersonatedOrgId;
    currentImpersonatedOrgId = impersonatedOrganization ?? null;

    resolvedFromProjection = !!projectedOrganization && projectedOrganization === organizationId;

    if (!organizationId) {
      dispatch(userNeedsSetupOrg());

      if (unsubscribeOrgUser) {
        unsubscribeOrgUser();
        unsubscribeOrgUser = null;
        currentOrgId = null;
      }

      dispatch(userProfileFetched({ ...rootUser, uid }));
    } else if (currentOrgId && currentOrgId !== organizationId && !impersonationChanged && followsProjection) {
      window.location.reload();
    } else {
      dispatch(userOrganizationSelected({ id: organizationId }));

      if (currentOrgId !== organizationId) {
        startOrgUserListener(organizationId);
      }
    }
  };

  const unsubscribeRootUser = userOnListen(
    uid,
    (rootUser: OrganizationUser) => {
      if (!rootUser) {
        return;
      }

      rootUserData = rootUser;

      const reduxClaims = getState?.()?.user?.claims;

      if (cachedFreshClaims && reduxClaims !== cachedFreshClaimsBasis) {
        cachedFreshClaims = undefined;
        cachedFreshClaimsBasis = undefined;
      }

      const claims = reduxClaims?.multi_role_enabled === true ? reduxClaims : (cachedFreshClaims ?? reduxClaims);
      const currentOrganization = rootUser?.currentOrganization as string | undefined;
      const projectionNeedsFreshClaims =
        claims?.multi_role_enabled !== true &&
        !!currentOrganization &&
        currentOrganization !== rootUser.defaultOrganization &&
        rootUser?.organizations?.[currentOrganization] === true &&
        refreshedClaimsFor !== currentOrganization &&
        (!!currentOrgId || !claims);

      if (projectionNeedsFreshClaims) {
        if (pendingRefreshFor === currentOrganization) return;

        pendingRefreshFor = currentOrganization;
        const generation = ++snapshotGeneration;

        readFreshClaims().then((freshClaims) => {
          if (generation === snapshotGeneration) pendingRefreshFor = null;
          if (disposed || generation !== snapshotGeneration) return;

          if (freshClaims) {
            refreshedClaimsFor = currentOrganization;
            cachedFreshClaims = freshClaims;
            cachedFreshClaimsBasis = getState?.()?.user?.claims;
          }

          handleRootUser(rootUserData ?? rootUser, freshClaims ?? claims);
        });
        return;
      }

      handleRootUser(rootUser, claims);
    },
    (error) => console.error('Root user listener error:', error.message),
    undefined
  );

  return () => {
    disposed = true;
    unsubscribeRootUser();
    if (unsubscribeOrgUser) {
      unsubscribeOrgUser();
    }
  };
};

export const fetchUserProfile = (uid) => async (dispatch) => {
  try {
    const userData = await getUserProfile(uid);
    if (userData) return dispatch(userProfileFetched(userData));

    return null;
  } catch (error) {
    console.log(error);
  }
};

export const provisionOrganizationProfile = (userData) => async (dispatch) => {
  try {
    dispatch(organizationProvisioningStarted());
    const createdUser = await startProvisioning(userData);
    if (createdUser) {
      dispatch(userProfileUpdated(createdUser));
      dispatch(organizationProvisioningComplete());
    }
    return createdUser;
  } catch (error) {
    console.error('provisionOrganizationProfile error', error);
    return false;
  }
};

export const updateUser = (organizationId, userId, data) => async (_dispatch) => {
  try {
    return updateOrganizationUser(organizationId, userId, data);
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    throw new Error(errorMessage || 'Unable to update user');
  }
};

export const changeUserPin = (organizationId, userId, type) => async (dispatch) => {
  try {
    const pin = await assignPin(userId, organizationId, type);
    await updateOrganizationUserPin(organizationId, userId, type, pin);
    dispatch(userPinUpdated());
    return pin;
  } catch (error) {
    console.error(error);
  }
};
